import { format, getYear } from "date-fns";
import z from "zod";

import { queryClient } from "@App";
import { getWeekObject } from "@lib/attendance/filters/dateRange";
import { i18n } from "@lib/i18n";
import { queries } from "@lib/queries";
import { globalLayoutRoute } from "@routes";
import { redirectToAccessFallback, requireScope } from "@routes/authorization";
import {
    api_pkg_attendance_dto_StatusSchema,
    api_pkg_attendance_dto_TypeSchema,
} from "@sdk/schemas";
import { createRoute, lazyRouteComponent } from "@tanstack/react-router";

const NetworkAttendancesDateModeEnum = z.enum(["rolling", "day", "week", "month"]);
export type NetworkAttendancesDateModeUnion =
    (typeof NetworkAttendancesDateModeEnum)[keyof typeof NetworkAttendancesDateModeEnum];

const NetworkAttendancesSearchModeEnum = z.enum(["sider", "manager"]);
export type NetworkAttendancesSearchModeUnion =
    (typeof NetworkAttendancesSearchModeEnum)[keyof typeof NetworkAttendancesSearchModeEnum];

const NetworkAttendancesStatusesSchema = z
    .array(api_pkg_attendance_dto_StatusSchema)
    .default(["att.waiting"]);

const NetworkAttendancesSearchSchema = z.object({
    // V2 params
    statuses: NetworkAttendancesStatusesSchema,
    siderIds: z.array(z.string()).optional(),
    managerIds: z.array(z.string()).optional(),

    // UI-only
    type: z.array(api_pkg_attendance_dto_TypeSchema).optional(),

    // date
    dateMode: NetworkAttendancesDateModeEnum.default("rolling"),
    rollingDays: z.enum(["7", "15", "30"]).catch("7"),
    day: z.coerce.date().default(() => new Date()),
    week: z.preprocess(
        (v) => (typeof v === "string" && /^\d{1,2}-\d{4}$/.test(v) ? v : undefined),
        z.string().default(() => getWeekObject(new Date()).objectID),
    ),
    month: z.preprocess(
        (v) => (typeof v === "string" && /^\d{2}-\d{4}$/.test(v) ? v : undefined),
        z.string().default(() => `${format(new Date(), "MM")}-${getYear(new Date())}`),
    ),

    // search
    searchMode: NetworkAttendancesSearchModeEnum.default("sider"),
    search: z.string().optional(),
});

export const networkAttendancesRoute = createRoute({
    getParentRoute: () => globalLayoutRoute,
    path: `network/attendances`,
    component: lazyRouteComponent(() => import("./NetworkAttendances"), "NetworkAttendances"),
    validateSearch: NetworkAttendancesSearchSchema,
    beforeLoad: async () => {
        requireScope("network");

        const isLogas = Boolean(localStorage.getItem("side_team_logasId"));
        const featuresPolicy = await queryClient.fetchQuery(queries.company.featuresPolicy());
        const networkLevelAttendancesPolicy = featuresPolicy?.networkLevelAttendancesPolicy;
        const canAccessByLogasPolicy = networkLevelAttendancesPolicy?.logasOnly ? isLogas : true;

        if (!networkLevelAttendancesPolicy?.enabled || !canAccessByLogasPolicy) {
            redirectToAccessFallback();
        }

        return { getTitle: () => i18n.doc_title_timesheets() };
    },
});
